import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import itPortalService from "../services/itPortalService";
import SideBar from "../components/SideBar";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import { MdOutlineShield, MdVpnKey, MdOutlineMailOutline } from "react-icons/md";
import "../styles/ManageMember.css";
import "../styles/ManageUserTopCoin.css";

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

export default function ITPortalLogin() {
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const navigate = useNavigate();

  const showSnack = (message, severity = "success") => {
    setSnack({ open: true, message, severity });
  };

  const handleSnackClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnack((prev) => ({ ...prev, open: false }));
  };

  const handleSendOtp = async () => {
    setIsLoading(true);
    setError("");
    try {
      const response = await itPortalService.sendOtp();
      if (response.status === 200 || response.status === 201) {
        setOtpSent(true);
        showSnack(response.data?.message || "Verification OTP has been sent to your registered email.");
      }
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to send OTP.";
      setError(msg);
      showSnack(msg, "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (!otp.trim()) {
      setError("Please enter the verification code.");
      return;
    }

    setIsLoading(true);
    setError("");
    try {
      const response = await itPortalService.verifyOtp(otp.trim());
      if (response.status === 200 || response.status === 201) {
        showSnack("Access verified successfully!");
        navigate("/it-portal/user-topcoin");
      }
    } catch (err) {
      const msg = err.response?.data?.message || "Invalid or expired OTP. Please try again.";
      setError(msg);
      showSnack(msg, "error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="manage-member-layout">
      <SideBar />

      <div className="main-content">
        {/* Header Bar matching SuperAdmin style */}
        <div className="header-bar" style={{ border: 0, boxShadow: "none" }}>
          <div className="header-top">
            <h2 className="header-title">IT Portal Access Verification</h2>
          </div>
        </div>

        {/* Verification Card */}
        <div
          className="it-card-container"
          style={{ maxWidth: "520px", margin: "25px auto 40px auto", textAlign: "center" }}
        >
          <div
            style={{
              width: "60px",
              height: "60px",
              borderRadius: "50%",
              background: "#eff6ff",
              color: "rgba(0, 0, 160, 1)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "30px",
              margin: "0 auto 16px auto",
            }}
          >
            <MdOutlineShield />
          </div>

          <h3 className="it-card-title" style={{ textAlign: "center", border: "none", paddingBottom: 0 }}>
            Secondary Authentication
          </h3>
          <p className="it-card-desc" style={{ marginBottom: "24px" }}>
            The IT Portal contains high-level configurations. Complete the one-time passcode verification to access the portal.
          </p>

          {error && (
            <div
              style={{
                color: "#dc2626",
                marginBottom: "20px",
                padding: "12px 16px",
                background: "#fef2f2",
                border: "1px solid #fecaca",
                borderRadius: "8px",
                fontSize: "14px",
                textAlign: "left",
              }}
            >
              {error}
            </div>
          )}

          {!otpSent ? (
            <div>
              <button
                type="button"
                onClick={handleSendOtp}
                disabled={isLoading}
                className="action-btn"
                style={{
                  width: "100%",
                  height: "46px",
                  fontSize: "15px",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                  marginTop: "10px",
                }}
              >
                <MdOutlineMailOutline size={20} />
                {isLoading ? "Sending Code..." : "Send Verification Code to Email"}
              </button>
            </div>
          ) : (
            <form onSubmit={handleVerifyOtp} style={{ textAlign: "left" }}>
              <div className="it-form-field">
                <label style={{ textAlign: "center", display: "block" }}>
                  Enter 6-Digit Verification Code
                </label>
                <input
                  type="text"
                  placeholder="• • • • • •"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  style={{
                    fontSize: "20px",
                    letterSpacing: "6px",
                    textAlign: "center",
                    fontWeight: "700",
                    height: "48px",
                  }}
                  maxLength={10}
                  required
                  autoFocus
                />
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="action-btn"
                style={{
                  width: "100%",
                  height: "46px",
                  fontSize: "15px",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                  marginTop: "10px",
                }}
              >
                <MdVpnKey size={18} />
                {isLoading ? "Verifying..." : "Verify & Enter IT Portal"}
              </button>

              <div style={{ textAlign: "center", marginTop: "16px" }}>
                <button
                  type="button"
                  onClick={handleSendOtp}
                  disabled={isLoading}
                  style={{
                    background: "none",
                    border: "none",
                    color: "rgba(0, 0, 160, 1)",
                    fontSize: "14px",
                    fontWeight: "600",
                    cursor: isLoading ? "not-allowed" : "pointer",
                    textDecoration: "underline",
                  }}
                >
                  Didn't receive the code? Resend OTP
                </button>
              </div>
            </form>
          )}
        </div>
      </div>

      {/* Snackbar Notification */}
      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={handleSnackClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleSnackClose}
          severity={snack.severity}
          sx={{ width: "100%" }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </div>
  );
}
